import { db, functions } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Submit a new-student intake (no payment). Calls the submitInquiry Cloud
// Function, which validates, saves a lead (source:'inquiry_form'), and emails
// the studio — plus the parent, UNLESS the payload carries
// stage:'register-step1', which marks a mid-form /register capture whose
// confirmation is sent later by submitRegistrationRequest instead.
// Mirrors the joinWorkshopWaitlist callable pattern.
// Throws on validation / config errors. Returns { ok: true }.
export async function submitInquiry(payload) {
	const callable = httpsCallable(functions, 'submitInquiry');
	const res = await callable(payload);
	return res.data;
}

// Submit a general contact message (no payment, no student). Reuses the SAME
// submitInquiry callable + leads collection (one system of record) via
// inquiryKind:'contact'; the server routes the notification to sing@. Throws on
// validation / config errors. Returns { ok: true }.
export async function submitContact(payload) {
	const callable = httpsCallable(functions, 'submitInquiry');
	const res = await callable({ ...payload, inquiryKind: 'contact' });
	return res.data;
}

// Lesson interest is an array on fall-2026+ leads/requests and a plain string
// on older docs — normalize either shape to an array for display/conversion.
// (Conversion uses the first entry as the student's primary instrument.)
export function interestList(value) {
	if (Array.isArray(value)) return value.filter(Boolean);
	return value ? [value] : [];
}

// ── Admin (employee portal) reads/writes ──

// Inquiry-form leads (new-student registrations), newest first. Mirrors
// packService.fetchPackLeads's direct-query read path; leads are readable by
// employee/admin per firestore.rules. Sort client-side so we don't need a
// composite index on (source, createdAt).
// `source` is an `in` filter rather than an equality because free-trial
// bookings are a second, deliberately distinct funnel entry: they carry
// `source: 'trial_booking'` so the Meta Pixel / lead-source question stays
// answerable, and collapsing them into 'inquiry_form' would have made that
// worse. They belong on THIS page regardless — they arrive with the existing
// `trial` funnel status, which is what that status was always for.
//
// An `in` on a single field needs no composite index. Note the other lead
// sources ('mms_waitlist', 'lesson-pack', 'workshop-registration') remain
// deliberately excluded — this page is the inquiry funnel, not every lead.
//
// `isTest` leads came from the /trial?test=1 exercise path (page deleted
// 2026-09-03; the docs remain) and must never
// reach this queue, or the safe way to test the booking flow stops being safe.
const INQUIRY_SOURCES = ['inquiry_form', 'trial_booking'];

export async function fetchInquiryLeads() {
	const q = query(collection(db, 'leads'), where('source', 'in', INQUIRY_SOURCES));
	const snap = await getDocs(q);
	const tsToMs = (ts) =>
		ts?.toMillis?.() ?? (ts?.seconds ? ts.seconds * 1000 : 0);
	return snap.docs
		.map((d) => ({ id: d.id, ...d.data() }))
		.filter((l) => l.isTest !== true)
		.sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt));
}

// Admin: move an inquiry lead through its funnel
// (new | contacted | trial | offered | archived; 'reviewed' is legacy).
// Routes through the setInquiryStatus callable (leads is write:false).
export async function setInquiryStatus(leadId, status) {
	const callable = httpsCallable(functions, 'setInquiryStatus');
	const res = await callable({ leadId, status });
	return res.data;
}

// Admin: convert an inquiry lead into a Family + Student roster entries. Routes
// through the convertInquiryToStudents callable (leads/families/students writes
// are server-side). `payload` = { leadId, guardian:{firstName,lastName,email,
// phone,address}, students:[{firstName,lastName,age,instrument}] } — the
// employee-reviewed values from the convert dialog. Returns { ok, familyId,
// studentIds, archivedLeadIds } — the server archives the same guardian's
// other live funnel leads (duplicate-student guard) and names them so the
// page can update those rows in place.
export async function convertInquiryToStudents(payload) {
	const callable = httpsCallable(functions, 'convertInquiryToStudents');
	const res = await callable(payload);
	return res.data;
}
